if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const redirectToCheckout = formData.get('return_to') === '/checkout';
        let bogoGiftVariantId = formData.get('properties[_bogo_gift_variant_id]');
        let bogoGiftName = formData.get('properties[_bogo_gift_name]');
        const bogoOffer = this.closest('[data-bogo-offer]') || this.closest('product-info')?.querySelector('[data-bogo-offer]');
        const bogoPropertyLabel = bogoOffer?.dataset.bogoPropertyLabel || 'Free gift';

        // If the cart already contains a BOGO gift item, Shopify's automatic
        // discount won't apply a second time. Skip adding the gift so it isn't
        // added at full price, and remove the visible "Free gift:" property so
        // it doesn't appear in the cart for an un-discounted item.
        if (bogoGiftVariantId && document.querySelector('.cart-item[data-item-type="gift"]')) {
          bogoGiftVariantId = null;
          bogoGiftName = null;
          formData.delete(`properties[${bogoPropertyLabel}]`);
          formData.delete('properties[_bogo_gift_variant_id]');
          formData.delete('properties[_bogo_gift_name]');
        }

        const request = this.buildAddToCartRequest(formData, bogoGiftVariantId, bogoGiftName, bogoPropertyLabel);

        fetch(request.url, request.config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (redirectToCheckout) {
              window.location.href = '/checkout/';
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            if (redirectToCheckout) {
              window.location.href = '/checkout/';
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      buildAddToCartRequest(formData, bogoGiftVariantId, bogoGiftName, bogoPropertyLabel = 'Free gift') {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        // Remove return_to from FormData so it doesn't interfere with the AJAX response
        formData.delete('return_to');

        if (!this.cart) {
          delete config.headers['Content-Type'];
          config.body = formData;
          return { url: routes.cart_add_url, config };
        }

        this.cart.setActiveElement(document.activeElement);
        const sections = this.cart.getSectionsToRender().map((section) => section.id);
        const hasGiftVariant = Number.isFinite(Number(bogoGiftVariantId)) && Number(bogoGiftVariantId) > 0;

        if (!hasGiftVariant) {
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
          delete config.headers['Content-Type'];
          config.body = formData;
          return { url: routes.cart_add_url, config };
        }

        const lineItemProperties = {};
        for (const [key, value] of formData.entries()) {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (!propertyMatch || propertyMatch[1].startsWith('_')) continue;
          lineItemProperties[propertyMatch[1]] = value;
        }

        const mainVariantId = Number(formData.get('id'));
        const quantity = Number(formData.get('quantity')) || 1;
        const payload = {
          items: [
            {
              id: mainVariantId,
              quantity,
              properties: lineItemProperties,
            },
            {
              id: Number(bogoGiftVariantId),
              quantity: 1,
              properties: {
                _is_bogo_gift: 'true',
                _bogo_source_product_variant: String(mainVariantId),
              },
            },
          ],
          sections,
          sections_url: window.location.pathname,
        };

        config.body = JSON.stringify(payload);
        return { url: `${routes.cart_add_url}.js`, config };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
